import { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import Badge from '../../../../../../components/ui/badge/Badge';
import { getAllUsers } from '../../../../../../services/qc';
import {
  ASSIGNED_REVIEWER_DISPLAY_MAX,
  FILE_NAME_DISPLAY_MAX,
  QcTaskEllipsisText,
  QcTruncatedValueWithInfo,
  QcUserFieldWithInfo,
  REQ_ID_DISPLAY_MAX,
} from './QcTaskTableCellRenderers';
import {
  formatQcTaskCellValue,
  formatQcTaskDateTime,
  formatQcTaskDuration,
  formatQcTaskUserDisplayName,
} from './qcTaskTableFormatters';

function AssignedReviewerFilter({ column }) {
  const filterValue = column?.getFilterValue?.() ?? '';
  const selectedAuthId = String(filterValue ?? '').trim();

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const isUserTypingRef = useRef(false);

  const normalizeUser = useCallback((u) => {
    const auth_user_id =
      u?.auth_user_id ?? u?.authUserId ?? u?.auth_id ?? u?.authId ?? u?.id;
    if (auth_user_id == null) return null;
    return {
      ...u,
      auth_user_id: String(auth_user_id),
      name: u?.name != null ? String(u.name) : undefined,
    };
  }, []);

  useEffect(() => {
    if (!selectedAuthId) {
      setSelectedOption(null);
      return;
    }

    const found =
      options.find(
        (o) => String(o?.auth_user_id ?? '').trim() === selectedAuthId,
      ) ?? null;

    setSelectedOption(
      found ?? {
        auth_user_id: selectedAuthId,
        name: selectedAuthId,
      },
    );
    // Don't override what the user is actively typing.
    if (!isUserTypingRef.current) {
      setInputValue(found?.name ?? selectedAuthId);
    }
  }, [selectedAuthId, options]);

  const fetchUsers = useCallback(
    async (term) => {
      const q = term == null ? '' : String(term).trim();
      if (!q) {
        setOptions([]);
        return;
      }

      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const users = await getAllUsers(q);
        if (seq !== requestSeqRef.current) return;
        const normalized = Array.isArray(users)
          ? users.map(normalizeUser).filter(Boolean)
          : [];
        setOptions(normalized);
      } catch {
        if (seq !== requestSeqRef.current) return;
        setOptions([]);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    [normalizeUser],
  );

  const handleInputChange = useCallback(
    (_event, newInput, reason) => {
      if (reason === 'clear') {
        isUserTypingRef.current = false;
        setInputValue('');
        setOptions([]);
        return;
      }
      if (reason !== 'input') return;
      isUserTypingRef.current = true;
      setInputValue(newInput);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void fetchUsers(newInput);
      }, 350);
    },
    [fetchUsers],
  );

  const handleSelectionChange = useCallback(
    (_event, newValue) => {
      const authId = newValue?.auth_user_id
        ? String(newValue.auth_user_id).trim()
        : '';
      isUserTypingRef.current = false;
      setSelectedOption(newValue ?? null);
      setInputValue(newValue?.name ?? authId);
      column?.setFilterValue?.(authId);
    },
    [column],
  );

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedOption}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelectionChange}
      getOptionLabel={(opt) => {
        const name = opt?.name != null ? String(opt.name).trim() : '';
        if (name) return name;
        const id = opt?.auth_user_id != null ? String(opt.auth_user_id) : '';
        return id;
      }}
      isOptionEqualToValue={(opt, val) =>
        String(opt?.auth_user_id ?? '') === String(val?.auth_user_id ?? '')
      }
      disablePortal
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Search reviewer"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiInput-root': { borderRadius: 0 },
            '& .MuiInput-underline:before': {
              borderBottomColor: 'rgba(0,0,0,0.25)',
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: 'rgba(0,0,0,0.4)',
            },
            '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5' },
          }}
        />
      )}
      sx={{ minWidth: 220 }}
    />
  );
}

/**
 * Material React Table column definitions for MT-QC-TASK-001.
 * Defined once at module scope so the array reference never changes (fewer table re-inits).
 */
export const getQcTaskTableColumns = (onOpenTask) => [
  {
    id: 'serial',
    header: 'S.No',
    size: 65,
    minSize: 40,
    enableSorting: false,
    enableColumnActions: false,
    Cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;
      return (
        <QcTaskEllipsisText>
          {pageIndex * pageSize + row.index + 1}
        </QcTaskEllipsisText>
      );
    },
  },
  {
    id: 'taskAction',
    header: 'Task',
    size: 95,
    minSize: 60,
    maxSize: 110,
    enableSorting: false,
    enableColumnActions: false,
    /** Avoid MRT copy wrapper (`<button>`) around the Open control — would nest `<button>` inside `<button>` (hydration error). */
    enableClickToCopy: false,
    Cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Badge
          variant="light"
          color="success"
          size="sm"
          onClick={() => onOpenTask?.(row.original)}
          aria-label="Open QC task"
          className="shrink-0 scale-90 sm:scale-95 md:scale-100"
        >
          Open
        </Badge>
      </Box>
    ),
  },
  {
    accessorKey: 'duration',
    header: 'Duration',
    size: 150,
    minSize: 110,
    maxSize: 180,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskDuration(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'state',
    header: 'State',
    size: 150,
    minSize: 110,
    maxSize: 180,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskCellValue(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'district',
    header: 'District',
    size: 150,
    minSize: 110,
    maxSize: 180,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskCellValue(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'language',
    header: 'Language',
    size: 150,
    minSize: 110,
    maxSize: 180,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskCellValue(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'source_file',
    header: 'Source File',
    size: 210,
    minSize: 150,
    maxSize: 280,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskCellValue(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'file_name',
    header: 'File Name',
    size: 210,
    minSize: 130,
    maxSize: 320,
    Cell: ({ cell }) => (
      <QcTruncatedValueWithInfo
        value={cell.getValue()}
        maxChars={FILE_NAME_DISPLAY_MAX}
        infoAriaLabel="Show full file name"
      />
    ),
  },
  {
    id: 'assigned_to',
    accessorFn: (row) => formatQcTaskUserDisplayName(row.assigned_to),
    header: 'Assignee',
    size: 190,
    minSize: 130,
    maxSize: 280,
    Cell: ({ row }) => (
      <QcUserFieldWithInfo
        user={row.original.assigned_to}
        maxChars={ASSIGNED_REVIEWER_DISPLAY_MAX}
        infoAriaLabel="Assignee details"
      />
    ),
  },
  {
    accessorKey: 'req_id',
    header: 'Request ID',
    size: 210,
    minSize: 160,
    maxSize: 280,
    Cell: ({ cell }) => (
      <QcTruncatedValueWithInfo
        value={cell.getValue()}
        maxChars={REQ_ID_DISPLAY_MAX}
        infoAriaLabel="Show full request ID"
      />
    ),
  },
  {
    id: 'assigned_reviewer',
    accessorFn: (row) => formatQcTaskUserDisplayName(row.assigned_reviewer),
    header: 'Assigned Reviewer',
    size: 220,
    minSize: 160,
    maxSize: 300,
    filterVariant: 'autocomplete',
    Filter: ({ column }) => <AssignedReviewerFilter column={column} />,
    Cell: ({ row }) => (
      <QcUserFieldWithInfo
        user={row.original.assigned_reviewer}
        maxChars={ASSIGNED_REVIEWER_DISPLAY_MAX}
        infoAriaLabel="Assigned reviewer details"
      />
    ),
  },
  {
    accessorKey: 'assigned_at',
    header: 'Assigned At',
    size: 180,
    minSize: 150,
    maxSize: 300,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskDateTime(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    size: 180,
    minSize: 150,
    maxSize: 300,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskDateTime(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
  {
    accessorKey: 'assignment_id',
    header: 'Assignment ID',
    size: 170,
    minSize: 130,
    maxSize: 220,
    Cell: ({ cell }) => (
      <QcTaskEllipsisText>
        {formatQcTaskCellValue(cell.getValue())}
      </QcTaskEllipsisText>
    ),
  },
];
